// Drag-to-link hook
// HTML5 drag-and-drop for cross-linking between problem and solution spaces

import { useCallback, useState, useRef } from 'react'
import type { CrossLinkEntityType } from '@ispot/shared'
import { VALID_CROSS_LINKS } from '@ispot/shared'

export interface DragSource {
  type: CrossLinkEntityType
  id: string
  title: string
  space: 'problem' | 'solution'
}

export function useDragToLink(onDrop: (
  sourceType: string, sourceId: string,
  targetType: string, targetId: string,
) => void) {
  const [isDragging, setIsDragging] = useState(false)
  const [dragSource, setDragSource] = useState<DragSource | null>(null)
  const [dropTargetId, setDropTargetId] = useState<string | null>(null)
  const dragDataRef = useRef<DragSource | null>(null)

  const canDrag = useCallback((type: string): boolean => {
    return type in VALID_CROSS_LINKS
  }, [])

  const isValidDropTarget = useCallback((targetType: string, targetSpace: 'problem' | 'solution'): boolean => {
    if (!dragDataRef.current) return false
    // Must be in the opposite space
    if (dragDataRef.current.space === targetSpace) return false
    const validTargets = VALID_CROSS_LINKS[dragDataRef.current.type as CrossLinkEntityType]
    return validTargets?.includes(targetType as CrossLinkEntityType) ?? false
  }, [])

  const handleDragStart = useCallback((source: DragSource, e: React.DragEvent) => {
    dragDataRef.current = source
    setDragSource(source)
    setIsDragging(true)
    e.dataTransfer.effectAllowed = 'link'
    e.dataTransfer.setData('text/plain', source.id)
  }, [])

  const handleDragOver = useCallback((targetId: string, targetType: string, targetSpace: 'problem' | 'solution', e: React.DragEvent) => {
    if (!isValidDropTarget(targetType, targetSpace)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'link'
    setDropTargetId(targetId)
  }, [isValidDropTarget])

  const handleDragLeave = useCallback(() => {
    setDropTargetId(null)
  }, [])

  const handleDrop = useCallback((targetType: string, targetId: string, e: React.DragEvent) => {
    e.preventDefault()
    const source = dragDataRef.current
    if (!source) return

    onDrop(source.type, source.id, targetType, targetId)
    setIsDragging(false)
    setDragSource(null)
    setDropTargetId(null)
    dragDataRef.current = null
  }, [onDrop])

  const handleDragEnd = useCallback(() => {
    setIsDragging(false)
    setDragSource(null)
    setDropTargetId(null)
    dragDataRef.current = null
  }, [])

  return {
    isDragging,
    dragSource,
    dropTargetId,
    canDrag,
    isValidDropTarget,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  }
}
